import PropTypes from 'prop-types'
import React, { Component } from 'react'
import Helmet from 'react-helmet'
import ReactCSSTransitionGroup from 'react-transition-group/CSSTransitionGroup'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'
import styles from './_subscriptionSettings.css'
import premiumStyles from './_premium.css'
import LineItem from './LineItem'
import {
  FormattedNumber,
  FormattedMessage,
  FormattedHTMLMessage,
  FormattedDate,
  injectIntl,
  defineMessages,
} from 'react-intl'
import CCForm from './CCForm'
import Loader from 'components/util/Loader'
import Modal from 'components/util/Modal'
import * as premiumActions from 'services/premium/actions'
import * as premium from 'services/premium/selectors'
import BillingProfile from './BillingProfile'
import { FormattedDuration } from 'shared/units'
import { LONG, SECONDS } from 'shared/units/constants'
import sharedMessages from './messages'

const mapStateToProps = (state) => ({
  subscription: premium.selectSubscription(state),
  subInterval: premium.selectSubInterval(state),
  subUnit: premium.selectSubUnit(state),
  subAutoRenew: premium.selectSubAutoRenew(state),
  billingProfiles: premium.selectBillingProfiles(state),
  subEndDate: premium.selectSubEndDate(state),
  subStartDate: premium.selectSubStartDate(state),
  subId: premium.selectSubId(state),
  submitting: premium.selectSubmitting(state),
  submittingAction: premium.selectSubmittingAction(state),
  success: premium.selectSuccess(state),
  errorMessage: state.premium.errorMessage,
  subscriptionSettingsLoading: premium.selectSubscriptionSettingsLoading(state),
  activeSubscription: premium.selectSubscription(state),
  platform: premium.selectSubPlatform(state),
  isMobile: premium.selectSubIsMobile(state),
  isPromo: premium.selectSubIsPromo(state),
  isTrial: premium.selectSubIsTrial(state),
  currency: premium.selectSubCurrency(state),
  subAmount: premium.selectSubAmount(state),
  subDuration: premium.selectSubDuration(state),
  premiumUpsellURL: premium.selectUpsellURL(state),
  autoRenewalLoading: premium.selectAutoRenewalLoading(state),
  autoRenewalSuccess: premium.selectAutoRenewalSuccess(state),
  autoRenewalError: premium.selectAutoRenewalError(state),
})

const mapDispatchToActions = {
  ...premiumActions,
  push,
}

const messages = defineMessages({
  'off': {
    id: 'premium.subscriptions.auto_renew_status.off',
    defaultMessage: 'Off',
  },
  'on': {
    id: 'premium.subscriptions.auto_renew_status.on',
    defaultMessage: 'On',
  },
})

export class SubscriptionSettings extends Component {

  static propTypes = {
    trialEndDate: PropTypes.object,
    subAmount: PropTypes.number,
    intl: PropTypes.object,
    currency: PropTypes.string,
    isTrial: PropTypes.bool,
    addCard: PropTypes.func,
    addBillingProfile: PropTypes.func,
    subUnit: PropTypes.string,
    subInterval: PropTypes.number,
    subEndDate: PropTypes.object,
    subStartDate: PropTypes.object,
    subAutoRenew: PropTypes.bool,
    fetchPaidSubscriptions: PropTypes.func,
    fetchBillingProfiles: PropTypes.func,
    billingProfiles: PropTypes.array,
    subId: PropTypes.string,
    updateAutoRenewal: PropTypes.func,
    deleteBillingProfile: PropTypes.func,
    success: PropTypes.bool,
    submitting: PropTypes.bool,
    submittingAction: PropTypes.string,
    updateDefaultBillingProfile: PropTypes.func,
    subscriptionSettingsLoading: PropTypes.bool,
    loadSubscriptionSettings: PropTypes.func,
    push: PropTypes.func,
    deletingBillingProfiles: PropTypes.array,
    platform: PropTypes.string,
    isMobile: PropTypes.bool,
    isPromo: PropTypes.bool,
    subDuration: PropTypes.number,
    premiumUpsellURL: PropTypes.string,
    updateBillingProfile: PropTypes.func,
    autoRenewalLoading: PropTypes.bool,
    autoRenewalSuccess: PropTypes.bool,
    autoRenewalError: PropTypes.bool,
    subscription: PropTypes.object,
  }

  state = {
    showNewCardForm: false,
    paidSubscriptionsLoading: false,
    paidSubscriptionsError: false,
    billingProfilesError: false,
    billingProfilesLoading: false,
  }

  componentWillMount () {
    this.setState({
      paidSubscriptionsLoading: true,
      billingProfilesLoading: true,
    })

    this.props.fetchPaidSubscriptions()
      .then(this.redirectIfNotPremium)
      .then(() => this.setState({ paidSubscriptionsLoading: false }))
      .catch(() => this.setState({
        paidSubscriptionsError: true,
        paidSubscriptionsLoading: false,
      }))

    this.props.fetchBillingProfiles()
      .then(() => this.setState({ billingProfilesLoading: false }))
      .catch(() => this.setState({
        billingProfilesError: true,
        billingProfilesLoading: false,
      }))

  }

  componentWillReceiveProps (nextProps) {
    const { submitting } = this.props
    if (submitting && !nextProps.submitting && nextProps.success) {
      this.setState({ showNewCardForm: false })
    }
  }

  redirectIfNotPremium = () => {
    // if we finish loading the subscription settings, and it turns out the user
    // does NOT have a subscription, send them to the upsell page
    const { subscription, premiumUpsellURL } = this.props
    if (!subscription) { window.location.href = premiumUpsellURL }
  }

  handleToggleDefaultBilling = (profile) => {
    this.props.updateDefaultBillingProfile(profile.billing_profile_id)
  }

  handleDeleteBillingProfile = (profile) => {
    this.props.deleteBillingProfile(profile.billing_profile_id)
  }

  handleToggleCCForm = () => {
    this.setState({ showNewCardForm: !this.state.showNewCardForm })
    // close card editing form, if user clicks 'add card'
    if (!this.state.showNewCardForm && this.state.isEditingCard) {
      this.setState({ isEditingCard: false })
    }
  }

  handleAutoRenewChange = ({ target: { value }}) => {
    const shouldRenew = value === '1'
    this.props.updateAutoRenewal({
      subscriptionId: this.props.subId,
      autoRenewal: shouldRenew,
    })
  }

  handleEditBillingProfile = (profile) => () => {
    this.setState({ isEditingCard: true, profileToEdit: profile })
    // close new card form, if user clicks 'edit'
    if (this.state.showNewCardForm) {
      this.setState({ showNewCardForm: false })
    }
  }

  handleCancelEditBillingProfile = () => {
    this.setState({ isEditingCard: false })
  }

  handleEditSubmit = (id) => (card) => {
    this.props.updateBillingProfile(id, card)
      .then(() => this.setState({ isEditingCard: false }))
      .catch(() => this.setState({ isEditingCard: false }))
  }

  handleCancelNewCard = () => {
    this.setState({ showNewCardForm: false })
  }

  handleCCFormSubmit = (card) => {
    this.props.addBillingProfile(card)
      .then(() => this.setState({ showNewCardForm: false }))
      .catch(() => this.setState({ showNewCardForm: false }))
  }

  render () {
    const {
      subUnit,
      subInterval,
      subEndDate,
      subStartDate,
      subAutoRenew,
      billingProfiles,
      submitting,
      submittingAction,
      platform,
      isMobile,
      isPromo,
      currency,
      intl,
      isTrial,
      subAmount,
      subDuration,
      autoRenewalLoading,
      autoRenewalSuccess,
      autoRenewalError,
    } = this.props

    const {
      profileToEdit,
      isEditingCard,
      showNewCardForm,
      billingProfilesError,
      billingProfilesLoading,
      paidSubscriptionsError,
      paidSubscriptionsLoading,
    } = this.state

    return (
      <div className={styles.container}>

        <Helmet title={intl.formatMessage({
          id: 'premium.subscriptions.title',
          defaultMessage: 'Subscription Settings',
        })} />

        <Modal isOpen={submitting}>
          <div className={premiumStyles.loadingModal}>
            <div>
              <Loader height={50} width={50} />
            </div>
            <h3 className={premiumStyles.loadingTitle}>
              {sharedMessages.submittingActions[submittingAction] ||
                <FormattedMessage
                  id='premium.subscriptions.submitting'
                  defaultMessage='Processing...'
                />
              }
            </h3>
          </div>
        </Modal>

        {paidSubscriptionsLoading ?
          <div className={styles.pageLoader}>
            <Loader type='pulse' height={60} width={60} style={{ marginTop: 200 }} />
          </div>
        : null}

        <h2 className={styles.title}>
          <FormattedMessage
            id='premium.subscriptions.headline'
            defaultMessage='Subscription settings'
          />
        </h2>

        {!paidSubscriptionsLoading && paidSubscriptionsError ?
          <div className={styles.loadingError}>
            <FormattedMessage
              id='premium.subscriptions.error.paid_subscriptions_error'
              defaultMessage='Sorry, we were unable to load your subscription information.'
            />
          </div>
        : null}

        {!paidSubscriptionsLoading && !paidSubscriptionsError ?
          <div>
            <LineItem>
              <div>
                <FormattedMessage
                  id='premium.subscriptions.plan'
                  defaultMessage='Plan'
                />
              </div>
              <div className={styles.planDetails}>
                {!isPromo ?
                  <FormattedMessage
                    id='premium.subscriptions.plan_details'
                    defaultMessage='{duration} MVP'
                    values={{
                      duration: (
                        <FormattedDuration
                          from={subUnit}
                          value={subInterval}
                          digits={0}
                          unit={LONG}
                        />
                      ),
                    }}
                  />
                : null}

                {isPromo && subDuration ?
                  <FormattedMessage
                    id='premium.subscriptions.plan_details_promo'
                    defaultMessage='Free {duration} plan'
                    values={{
                      duration: (
                        <FormattedDuration
                          from={SECONDS}
                          value={subDuration}
                          digits={0}
                          unit={LONG}
                        />
                      ),
                    }}
                  />
                : null}
              </div>
            </LineItem>
            <LineItem>
              <div>
                <FormattedMessage
                  id='premium.subscriptions.plan_price'
                  defaultMessage='Price'
                />
              </div>
              <div>
                {currency ?
                  <FormattedNumber
                    value={isTrial ? 0 : subAmount}
                    style='currency'
                    currency={currency}
                    currencyDisplay='symbol'
                  />
                : null}
              </div>
            </LineItem>

            {isPromo ?
              <div>
                <LineItem>
                  <div>
                    <FormattedMessage
                      id='premium.subscriptions.payment_method'
                      defaultMessage='Payment Method'
                    />
                  </div>
                  <div>
                    <FormattedMessage
                      id='premium.subscriptions.payment_method_none'
                      defaultMessage='None'
                    />
                  </div>
                </LineItem>
                <LineItem>
                  <FormattedMessage
                    id='premium.subscriptions.expires'
                    defaultMessage='Expires'
                  />
                  <div>
                    {subEndDate ?
                      <FormattedDate
                        value={subEndDate}
                        year='numeric'
                        month='long'
                        day='numeric'
                        weekday='long'
                      />
                    : null}
                  </div>
                </LineItem>
              </div>
            : null}

            {!isMobile && !isPromo ?
              <div>
                <LineItem>
                  <div>
                    <FormattedMessage
                      id='premium.subscriptions.payment_method'
                      defaultMessage='Payment Method'
                    />
                  </div>
                  <div>
                    <span onClick={this.handleToggleCCForm} className={styles.link}>
                      {showNewCardForm ?
                        <FormattedMessage
                          id='premium.subscriptions.hide_card'
                          defaultMessage='Hide new card form'
                        />
                      :
                        <FormattedMessage
                          id='premium.subscriptions.add_card'
                          defaultMessage='Add credit/debit card'
                        />
                      }
                    </span>
                  </div>
                </LineItem>

                {showNewCardForm ?
                  <LineItem>
                    <div style={{ marginRight: 0 }}>
                      <h3 className={styles.subhead}>
                        <FormattedMessage
                          id='premium.subscriptions.add_card_details'
                          defaultMessage='Add Card Payment Details'
                        />
                      </h3>
                      <CCForm
                        mode='create'
                        onSubmit={this.handleCCFormSubmit}
                        onCancel={this.handleCancelNewCard}
                      />
                    </div>
                  </LineItem>
                : null}

                {isEditingCard ?
                  <LineItem>
                    <div style={{ marginRight: 0 }}>
                      <h3 className={styles.subhead}>
                        <FormattedMessage
                          id='premium.subscriptions.edit_card_details'
                          defaultMessage='Edit card details'
                        />
                      </h3>
                      <CCForm
                        mode='edit'
                        defaultProfile={profileToEdit}
                        onSubmit={this.handleEditSubmit(profileToEdit.billing_profile_id)}
                        onCancel={this.handleCancelEditBillingProfile}
                      />
                    </div>
                  </LineItem>
                : null}

                {billingProfilesLoading ?
                  <div className={styles.well}>
                    <Loader type='ios' height={20} width={20} />
                  </div>
                : null}

                {!billingProfilesLoading && billingProfilesError ?
                  <div className={styles.loadingError}>
                    <FormattedHTMLMessage
                      id='premium.subscription.error.loading_billing_profiles'
                      defaultMessage={`
                        Sorry, we were unable to retrieve your billing information.
                        You can try again, or contact our support team
                        <a href="https://support.mapmyfitness.com/hc/en-us/requests/new" rel="noopener noreferrer" target="_blank">
                          contact our support team </a>
                        for further assistance.
                      `}
                    />
                  </div>
                : null}

                {!billingProfilesLoading && !billingProfilesError ?
                  <div>
                    {billingProfiles && !billingProfiles.length ?
                      <div className={styles.well}>
                        <FormattedMessage
                          id='premium.subscriptions.no_billing_profiles'
                          defaultMessage='Sorry, we couldn&apos;t find any credit cards on file'
                        />
                      </div>
                    : null}

                    {!isEditingCard ?
                      <ReactCSSTransitionGroup
                        transitionName='billingProfile'
                        transitionEnterTimeout={500}
                        transitionLeaveTimeout={300}
                      >
                        {billingProfiles && billingProfiles.map((profile, i) => (
                          <BillingProfile
                            index={i}
                            key={profile.billing_profile_id}
                            profile={profile}
                            onDelete={this.handleDeleteBillingProfile}
                            onSelect={this.handleToggleDefaultBilling}
                            onEdit={this.handleEditBillingProfile(profile)}
                            allowEdit
                          />
                        ))}
                      </ReactCSSTransitionGroup>
                    : null}
                  </div>
                : null}

                {isTrial ?
                  <LineItem>
                    <div className={premiumStyles.note} style={{ margin: 0 }}>
                      <FormattedMessage
                        id='premium.subscriptions.trial.note'
                        defaultMessage={`
                          You will not be charged during your free trial. Your
                          subscription will renew at {displayPrice} per month on {renewDate}
                        `}
                        values={{
                          displayPrice: (
                            <FormattedNumber
                              style='currency'
                              currencyDisplay='symbol'
                              value={subAmount}
                              currency={currency}
                            />
                          ),
                          renewDate: <FormattedDate value={subEndDate} />,
                        }}
                      />
                    </div>
                  </LineItem>
                : null}

                {!isPromo && !isTrial ?
                  <LineItem>
                    <FormattedMessage
                      id='premium.subscriptions.last_billed'
                      defaultMessage='Last Billed'
                    />
                    {subStartDate ?
                      <FormattedDate
                        value={subStartDate}
                        year='numeric'
                        month='long'
                        day='2-digit'
                      />
                    : null}
                  </LineItem>
                : null}

                <LineItem>
                  <div>
                    <p>
                      <FormattedMessage
                        id='premium.subscriptions.auto_renewal'
                        defaultMessage='Auto-Renewal'
                      />
                    </p>
                    <p>
                      {subEndDate && subAutoRenew ?
                        <FormattedMessage
                          id='premium.subscriptions.renews_on'
                          defaultMessage='Renews on {date}'
                          values={{
                            date: (
                              <FormattedDate
                                value={subEndDate}
                                year='numeric'
                                month='long'
                                day='2-digit'
                              />
                            ),
                          }}
                        />
                      : null}
                      {subEndDate && !subAutoRenew ?
                        <FormattedMessage
                          id='premium.subscriptions.ends_on'
                          defaultMessage='Ends on {date}'
                          values={{
                            date: (
                              <FormattedDate
                                value={subEndDate}
                                year='numeric'
                                month='long'
                                day='2-digit'
                              />
                            ),
                          }}
                        />
                      : null}
                    </p>
                  </div>
                  <div>
                    {autoRenewalLoading ?
                      <Loader type='ios' className={styles.autoRenewalLoading} />
                    : null}
                    {autoRenewalSuccess ?
                      <span className={styles.success}>
                        <FormattedMessage
                          className={styles.success}
                          id='premium.subscriptions.successfully_updated'
                          defaultMessage='Successfully updated'
                        />
                      </span>
                    : null}
                    {autoRenewalError ?
                      <span className={styles.error}>
                        <FormattedMessage
                          id='premium.subscriptions.error_updating'
                          defaultMessage='Sorry, there was an error.'
                        />
                      </span>
                    : null}
                    <select
                      disabled={autoRenewalLoading}
                      value={subAutoRenew ? '1' : '0'}
                      className={styles.select}
                      onChange={this.handleAutoRenewChange}
                    >
                      <option value='0'>{intl.formatMessage(messages.off)}</option>
                      <option value='1'>{intl.formatMessage(messages.on)}</option>
                    </select>
                  </div>
                </LineItem>

                <div className={styles.autoRenewNote}>
                  <FormattedMessage
                    id='premium.subscriptions.how_to_disable_autorenew'
                    defaultMessage='To cancel your subscription, turn auto-renew off'
                  />
                </div>

              </div>

            : null}

          </div>
        : null}

        {/* Mobile subscriptions, links to app stores */}

        {isMobile ?
          <LineItem>
            <FormattedMessage
              id='premium.subscriptions.payment_method'
              defaultMessage='Payment Method'
            />

            {platform === 'ios' ?
              <a
                className={styles.link}
                href='https://buy.itunes.apple.com/WebObjects/MZFinance.woa/wa/manageSubscriptions'>
                <FormattedMessage
                  id='premium.subscriptions.manage_on_itunes'
                  defaultMessage='Manage subscription on iTunes'
                />
              </a>
            : null}

            {platform === 'android' ?
              <a
                className={styles.link}
                href='https://play.google.com/apps?type=1&hl=en'>
                <FormattedMessage
                  id='premium.subscriptions.manage_on_android'
                  defaultMessage='Manage subscription on the Google Play'
                />
              </a>
            : null}

          </LineItem>
        : null}

      </div>
    )
  }
}

export const Page = injectIntl(SubscriptionSettings)
export default connect(mapStateToProps, mapDispatchToActions)(Page)



// WEBPACK FOOTER //
// ./src/components/premium/SubscriptionSettings.jsx